/**
 * Churn risk and promotion effectiveness: two panels that read, and change nothing.
 *
 * Churn is judged against each customer's own ordering rhythm, so "late" means late for
 * them. Promotion effectiveness compares discounted lines with full-price ones; discounts
 * here include tier discounts, which bigger buyers get anyway, so a lift is something to
 * look into rather than proof the discount caused it. Both panels say so on screen.
 */

import { useQuery } from '@tanstack/react-query'

import { DataTable, type Column } from '@/components/DataTable'
import {
  formatMoney,
  getChurnRisk,
  getPromotions,
  type ChurnRow,
  type DiscountBand,
  type ProductLift,
} from '@/features/analytics/api'
import { Kpi, PanelCard } from '@/features/analytics/panels'

const RISK_STYLE: Record<ChurnRow['risk'], string> = {
  high: 'bg-danger/10 text-danger',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-400',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
}

const churnColumns: Column<ChurnRow>[] = [
  {
    key: 'customer',
    header: 'Customer',
    className: 'font-medium',
    sortValue: (row) => row.customer_name,
    cell: (row) => row.customer_name,
  },
  {
    key: 'risk',
    header: 'Risk',
    sortValue: (row) => row.score,
    cell: (row) => (
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RISK_STYLE[row.risk]}`}>
        {row.risk} · {row.score}
      </span>
    ),
  },
  {
    key: 'since',
    header: 'Days since last order',
    className: 'tabular-nums',
    sortValue: (row) => row.days_since_last_order,
    cell: (row) => `${row.days_since_last_order} (usually ${Math.round(row.typical_gap_days)})`,
  },
  {
    key: 'trend',
    header: 'Last 90 days / prior 90',
    className: 'tabular-nums',
    sortValue: (row) => row.revenue_last_90,
    cell: (row) => `${formatMoney(row.revenue_last_90)} / ${formatMoney(row.revenue_prior_90)}`,
  },
  {
    key: 'why',
    header: 'Why',
    className: 'text-xs text-slate-500 dark:text-slate-400',
    cell: (row) => row.reasons.join('; '),
  },
]

export function ChurnPanel({ days = 365 }: { days?: number }) {
  const churn = useQuery({
    queryKey: ['analytics', 'churn', days],
    queryFn: () => getChurnRisk({ days, limit: 50 }),
    retry: false,
  })
  const data = churn.data

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 tablet:grid-cols-4">
        <Kpi label="High risk" value={data ? String(data.high) : '—'} loading={churn.isLoading} />
        <Kpi
          label="Medium risk"
          value={data ? String(data.medium) : '—'}
          loading={churn.isLoading}
        />
        <Kpi
          label="Customers scored"
          value={data ? String(data.customers) : '—'}
          loading={churn.isLoading}
        />
        <Kpi
          label="Revenue at risk"
          value={data ? formatMoney(data.revenue_at_risk) : '—'}
          hint="prior 90 days, high-risk customers"
          loading={churn.isLoading}
        />
      </div>
      <PanelCard
        title="Customers likely to churn"
        description={`Late against their own ordering rhythm, or spending much less than before. As of ${data?.as_of ?? '…'}.`}
        isLoading={churn.isLoading}
        error={churn.error}
        isEmpty={!data?.rows.length}
        emptyMessage="Every customer is ordering on their usual rhythm."
      >
        <DataTable
          rows={data?.rows ?? []}
          columns={churnColumns}
          getRowId={(row) => row.customer_id}
          caption="Customers at risk of churning, highest risk first"
          emptyMessage="Every customer is ordering on their usual rhythm."
        />
      </PanelCard>
    </div>
  )
}

const bandColumns: Column<DiscountBand>[] = [
  { key: 'band', header: 'Discount', className: 'font-medium', cell: (row) => row.band },
  { key: 'lines', header: 'Lines', className: 'tabular-nums', cell: (row) => row.lines },
  {
    key: 'upl',
    header: 'Units per line',
    className: 'tabular-nums',
    cell: (row) => row.units_per_line.toFixed(2),
  },
  {
    key: 'revenue',
    header: 'Revenue',
    className: 'tabular-nums',
    cell: (row) => formatMoney(row.revenue),
  },
  {
    key: 'discount',
    header: 'Discount given',
    className: 'tabular-nums',
    cell: (row) => formatMoney(row.discount_given),
  },
  {
    key: 'margin',
    header: 'Margin',
    className: 'tabular-nums',
    cell: (row) => `${row.margin_pct.toFixed(1)}%`,
  },
]

const liftColumns: Column<ProductLift>[] = [
  {
    key: 'product',
    header: 'Product',
    className: 'font-medium',
    sortValue: (row) => row.product_name,
    cell: (row) => row.product_name,
  },
  {
    key: 'lift',
    header: 'Lift',
    className: 'tabular-nums',
    sortValue: (row) => row.lift_pct,
    cell: (row) => `${row.lift_pct > 0 ? '+' : ''}${row.lift_pct.toFixed(1)}%`,
  },
  {
    key: 'units',
    header: 'Units per line (discounted / full)',
    className: 'tabular-nums',
    cell: (row) =>
      `${row.units_per_line_discounted.toFixed(2)} / ${row.units_per_line_full_price.toFixed(2)}`,
  },
  {
    key: 'lines',
    header: 'Lines (discounted / full)',
    className: 'tabular-nums text-slate-500 dark:text-slate-400',
    cell: (row) => `${row.discounted_lines} / ${row.full_price_lines}`,
  },
]

export function PromotionPanel({ days = 90 }: { days?: number }) {
  const promotions = useQuery({
    queryKey: ['analytics', 'promotions', days],
    queryFn: () => getPromotions({ days, limit: 10 }),
    retry: false,
  })
  const data = promotions.data
  const lift = data?.overall_lift_pct

  return (
    <div className="space-y-4">
      <PanelCard
        title="Do discounts sell more?"
        description={
          lift == null
            ? 'Discounted lines against full-price lines.'
            : `Discounted lines carry ${lift > 0 ? '+' : ''}${lift.toFixed(1)}% units per line against full price, for ${formatMoney(data?.discount_given)} given away. Tier discounts are included, so read this as a correlation.`
        }
        isLoading={promotions.isLoading}
        error={promotions.error}
        isEmpty={!data?.bands.some((band) => band.lines > 0)}
        emptyMessage="Nothing was sold in this window."
      >
        <DataTable
          rows={data?.bands ?? []}
          columns={bandColumns}
          getRowId={(row) => row.band}
          caption="Sales by discount depth"
        />
      </PanelCard>
      <PanelCard
        title="Where discounts move volume"
        description="Products with at least five lines each way, ranked by lift."
        isLoading={promotions.isLoading}
        error={promotions.error}
        isEmpty={!data?.best.length && !data?.worst.length}
        emptyMessage="Not enough discounted and full-price sales of the same product to compare."
      >
        <DataTable
          rows={[...(data?.best ?? []), ...(data?.worst ?? [])]}
          columns={liftColumns}
          getRowId={(row) => row.product_id}
          caption="Per-product lift from discounting"
        />
      </PanelCard>
    </div>
  )
}
